import { normalizeFilePathTarget } from './linkify'

export interface MarkdownLocalPathResolutionOptions {
  basePath?: string
  relativeFileBasePaths?: string[]
}

function isRemoteOrEmbeddedTarget(target: string): boolean {
  return /^(?:https?:|mailto:|ftp:|data:|#)/i.test(target)
}

function isAbsoluteLocalTarget(target: string): boolean {
  return /^file:\/\//i.test(target)
    || /^[A-Za-z]:[/\\]/.test(target)
    || /^\\\\/.test(target)
    || target.startsWith('/')
    || target.startsWith('~/')
}

function hasPathSeparator(target: string): boolean {
  return target.includes('/') || target.includes('\\')
}

function trimTrailingSeparators(path: string): string {
  return path.replace(/[\\/]+$/, '')
}

function trimLeadingRelativePrefix(path: string): string {
  return path.replace(/^\.[\\/]/, '').replace(/^[\\/]+/, '')
}

function joinLocalPath(basePath: string, relativePath: string): string {
  const normalizedBase = trimTrailingSeparators(basePath)
  const separator = /^(?:[A-Za-z]:|\\\\)/.test(normalizedBase) || normalizedBase.includes('\\')
    ? '\\'
    : '/'
  const normalizedRelative = trimLeadingRelativePrefix(relativePath)
    .replace(/[\\/]+/g, separator)
  return `${normalizedBase}${separator}${normalizedRelative}`
}

export function resolveMarkdownLocalPathTarget(
  target: string,
  options: MarkdownLocalPathResolutionOptions = {},
): string {
  const normalizedTarget = normalizeFilePathTarget(target)
  if (!normalizedTarget) return normalizedTarget
  if (isRemoteOrEmbeddedTarget(normalizedTarget) || isAbsoluteLocalTarget(normalizedTarget)) {
    return normalizedTarget
  }

  const contextualBasePaths = options.relativeFileBasePaths ?? []
  if (!hasPathSeparator(normalizedTarget) && contextualBasePaths.length > 0) {
    return joinLocalPath(contextualBasePaths[0]!, normalizedTarget)
  }

  if (options.basePath) {
    return joinLocalPath(options.basePath, normalizedTarget)
  }

  return normalizedTarget
}
